import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Typography,
  Space,
  Button,
  Alert,
  Row,
  Form,
  QRCode,
  Spin,
  QRCodeProps,
  Select,
  Input,
  Col,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { optionsContactIdd } from "../../constants/options";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { campaignService } from "../../services/campaignService";
import CustomModal from "./Modal";
import { CampaignRegisteredChannel } from "../../models/campaign";
// import { browserLog } from "../../utils/log";

export type WhatsAppConfigModalProps = {
  title: string;
  open: boolean;
  onCancel: () => void;
  onContinue: (values: {
    clientId: string;
    email: string;
    contactIdd: string;
    contactNumber: string;
  }) => void;
  defaultEmail: string;
  defaultContactIdd?: string;
  defaultContactNumber?: string;
  registeredChannel?: CampaignRegisteredChannel[] | null;
};

const { Title, Text } = Typography;

// After this window the QR auto-refresh stops and a manual retry is required.
const QR_RETRY_TIMEOUT_MS = 3 * 60 * 1000;

const formatCountdown = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const customStatusRender: QRCodeProps["statusRender"] = (info) => {
  switch (info.status) {
    case "expired":
      return (
        <div>
          <CloseCircleFilled style={{ color: "red" }} /> {info.locale?.expired}
          <p>
            <Button type="link" onClick={info.onRefresh}>
              <ReloadOutlined /> {info.locale?.refresh}
            </Button>
          </p>
        </div>
      );
    case "loading":
      return (
        <Space direction="vertical">
          <Spin size="large" />
          <p>Generating QR code...</p>
        </Space>
      );
    case "scanned":
      return (
        <div>
          <CheckCircleFilled style={{ color: "green" }} />{" "}
          {info.locale?.scanned}
        </div>
      );
    default:
      return null;
  }
};

const Scan: React.FC<WhatsAppConfigModalProps> = ({
  title,
  open,
  onCancel,
  onContinue,
  defaultEmail,
  defaultContactIdd,
  defaultContactNumber,
  registeredChannel,
}) => {
  const [form] = Form.useForm<{
    clientId: string;
    contactIddScan: string;
    contactNumberScan: string;
  }>();
  const [step, setStep] = useState<0 | 1>(0);
  const [active, setActive] = useState(false);
  const [valueQR, setValueQR] = useState("");
  const [loadingQR, setLoadingQR] = useState(false);
  const [checking, setChecking] = useState(false);
  const contactIdd = Form.useWatch("contactIddScan", form);
  const contactNumber = Form.useWatch("contactNumberScan", form);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [expiredQR, setExpiredQR] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrFailed, setQrFailed] = useState(false);
  const [qrTimedOut, setQrTimedOut] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    QR_RETRY_TIMEOUT_MS / 1000,
  );
  const [error, setError] = useState(true);
  const pollingRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const handleBack = () => {
    clearTimers();
    setExpiredQR(false);
    setIsGenerating(false);
    setQrTimedOut(false);
    if (title === "reactive") {
      onCancel();
    } else {
      setStep(0);
    }
  };

  const handleConnect = async () => {
    setChecking(true);
    try {
      checkActivate(true);
    } finally {
      setChecking(false);
    }
  };

  const clearTimers = () => {
    pollingRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  // Starts the 3-minute window. When it elapses we stop the auto-refresh loop
  // and surface a manual retry button.
  const startSessionTimer = () => {
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setQrTimedOut(false);
    setRemainingSeconds(QR_RETRY_TIMEOUT_MS / 1000);
    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    sessionTimeoutRef.current = setTimeout(() => {
      clearTimers();
      setLoadingQR(false);
      setQrTimedOut(true);
    }, QR_RETRY_TIMEOUT_MS);
  };

  const handleRetry = () => {
    setIsGenerating(false);
    setQrFailed(false);
    setLoadingQR(true);
    startSessionTimer();
    generateQR(
      contactIdd || defaultContactIdd || "",
      contactNumber || defaultContactNumber || "",
    );
  };

  const checkActivate = async (
    handleConnect: boolean = false,
    reactive: boolean = false,
    iddParam?: string,
    numberParam?: string,
  ) => {
    let idd = iddParam || contactIdd;
    let number = numberParam || contactNumber;
    if (reactive) {
      idd = iddParam || defaultContactIdd || "";
      number = numberParam || defaultContactNumber || "";
    }
    const data = await campaignService.checkActivate(idd + number);
    //CHANGE IF WANT BYPASS
    const bypass = false;
    if (data?.active === true || bypass) {
      setActive(true);
      clearTimers();
      // browserLog("info", "data active ", data);

      onContinue({
        clientId: contactIdd + contactNumber,
        email: defaultEmail,
        contactIdd: contactIdd,
        contactNumber: contactNumber,
      });
    } else {
      if (handleConnect) {
        setIsErrorModalVisible(true);
      } else {
        setExpiredQR(true);
      }
    }
  };
  const handleContinue = async () => {
    setStep(1);
    startSessionTimer();
    generateQR(contactIdd, contactNumber);
  };
  const generateQR = async (iddParam?: string, numberParam?: string) => {
    let idd = iddParam || contactIdd;
    let number = numberParam || contactNumber;

    if (defaultEmail && idd && number) {
      try {
        setLoadingQR(true);

        const data1 = await campaignService.initClientId(idd + number);
        if (data1?.status === "error") {
          return;
        } else {
          checkActivate(false, false, idd, number);
        }
      } catch (error) {
        console.error("Error generate QR:", error);
      } finally {
        setLoadingQR(false);
      }
    }
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      timeoutRef.current = setTimeout(resolve, ms);
    });

  const generateQRCode = async (
    contactIddTarget: string,
    contactNumberTarget: string,
  ): Promise<void> => {
    if (isGenerating) return;
    if (!contactIddTarget || !contactNumberTarget) {
      return;
    }
    pollingRef.current = true;

    setIsGenerating(true);
    setQrFailed(false);
    setExpiredQR(false);
    setLoadingQR(true);
    const tryGenerate = async (): Promise<void> => {
      if (!pollingRef.current) return;

      // browserLog("info", "Generate QR called");

      const data = await campaignService.generateQR(
        contactIddTarget + contactNumberTarget,
      );
      // browserLog("info", "Generate QR response", data);

      if (data?.status === "expired" || (data?.status === "no_qr" && active)) {
        // browserLog("info", "expired or no qr with active", data);

        setLoadingQR(true);
        await new Promise((resolve) => setTimeout(resolve, 2000));

        return tryGenerate();
      }
      if (
        data?.status === "error" &&
        (data?.message?.includes("not found") ||
          data?.message?.includes("not initialized"))
      ) {
        // browserLog("error", "QR session init failed", data);
        clearTimers();
        setQrFailed(true);
        setLoadingQR(false);
        setIsGenerating(false);
        return;
      } else {
        if (data != null && data?.status !== "no_qr") {
          const ttlMs = data.ttlMs ?? 2000;

          // browserLog("info", "no_qr and active", data);

          setValueQR(data?.qr);
          setLoadingQR(false);

          timeoutRef.current = setTimeout(() => {
            if (!pollingRef.current) return;
            setExpiredQR(true);
          }, ttlMs);
        }
        setIsGenerating(false);

        await sleep(2000);

        return tryGenerate();
      }
    };

    await tryGenerate();
  };

  useEffect(() => {
    if (
      expiredQR &&
      contactIdd &&
      contactNumber &&
      open &&
      step === 1 &&
      !qrTimedOut
    ) {
      generateQRCode(contactIdd, contactNumber);
    }
  }, [contactIdd, contactNumber, expiredQR, open, step, qrTimedOut]);

  useEffect(() => {
    if (title == "reactive") {
      setStep(1);
      if (defaultContactIdd != undefined && defaultContactNumber != undefined) {
        form.setFieldsValue({
          contactIddScan: defaultContactIdd,
          contactNumberScan: defaultContactNumber,
        });
        checkActivate(false, true, defaultContactIdd, defaultContactNumber);

        startSessionTimer();
        generateQR(defaultContactIdd, defaultContactNumber);
      }
    }
  }, [defaultContactIdd, defaultContactNumber, title]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  return (
    <>
      <Modal
        className="add-new-mobile-number-modal"
        open={open}
        onCancel={onCancel}
        footer={null}
        width={680}
        title={
          <Title level={4} style={{ margin: 0 }}>
            {title === "reactive"
              ? "Reactive WhatsApp"
              : "Add New Mobile Number"}
          </Title>
        }>
        <Title level={4} style={{ textAlign: "center", marginBottom: 4 }}>
          {step === 0
            ? title === "reactive"
              ? "Reactive WhatsApp"
              : "Add New Mobile Number"
            : "Scan QR Code"}
        </Title>
        <Text
          type="secondary"
          style={{ display: "block", textAlign: "center", marginBottom: 16 }}>
          {step === 0
            ? "Provide your WhatsApp-registered number to get started"
            : "Scan this QR code to verify your new mobile number"}
        </Text>
        {step === 0 && (
          <Alert
            className="warning-configuration-whatsapp"
            type="warning"
            message={<strong>Prerequisite</strong>}
            description={
              <span>
                Integration requires a verified WhatsApp Business account.{" "}
                <a
                  href="https://business.whatsapp.com/products/business-app-get-started"
                  target="_blank"
                  rel="noopener noreferrer">
                  Click here
                </a>{" "}
                to verify or set up your account.
              </span>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          requiredMark={(label, { required }) =>
            required ? (
              <>
                {label} <span style={{ color: "#ff4d4f" }}> {" *"}</span>
              </>
            ) : (
              label
            )
          }
          layout="vertical"
          style={{ marginTop: 12 }}
          preserve={true}>
          <div style={{ display: step === 0 ? "block" : "none" }}>
            <>
              <Form.Item
                label="Mobile Number"
                required
                extra={
                  <Text type="secondary">
                    This must be the WhatsApp-registered mobile number. You’ll
                    scan a QR code next to connect and enable campaign sending.
                  </Text>
                }
                style={{ marginBottom: 5 }}>
                <Space.Compact className="w-100">
                  <Form.Item
                    name="contactIddScan"
                    noStyle
                    initialValue="65"
                    rules={[
                      { required: true, message: "Select country code" },
                    ]}>
                    <Select
                      style={{ width: 150 }}
                      options={optionsContactIdd}
                      placeholder="Code"
                      onChange={() => {
                        form.validateFields(["contactNumberScan"]);
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="contactNumberScan"
                    noStyle
                    rules={[
                      { required: true, message: "Enter phone number" },
                      {
                        validator: async (_, value) => {
                          const code = form.getFieldValue("contactIddScan");
                          if (!code || !value) {
                            return Promise.resolve();
                          }

                          if (!/^\d+$/.test(value)) {
                            return Promise.reject(
                              new Error("Please enter a valid mobile number"),
                            );
                          }
                          const phone = parsePhoneNumberFromString(
                            `+${code}${value}`,
                            "SG",
                          );
                          if (!phone || !phone.isValid()) {
                            setError(true);
                            return Promise.reject(
                              new Error("Please enter a valid mobile number"),
                            );
                          }
                          // check is already exist
                          const exists = registeredChannel?.some(
                            (x) =>
                              x.Status === true &&
                              x.ContactNumber === phone.nationalNumber &&
                              x.ContactIdd === code,
                          );

                          if (exists) {
                            setError(true);
                            return Promise.reject(
                              new Error(
                                "This number is already registered for this channel",
                              ),
                            );
                          }
                          setError(false);

                          return Promise.resolve();
                        },
                      },
                    ]}>
                    <Input
                      placeholder="Enter Phone Number"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
              {/* <Alert
                type="info"
                description={
                  <span>
                    WhatsApp sessions are time-limited. Once a session expires,
                    you will receive an email notification prompting you to
                    reactivate the connection by scanning a new QR code.
                  </span>
                }
                showIcon
                style={{ marginBottom: 16 }}
              /> */}
            </>
          </div>

          <div style={{ display: step === 1 ? "block" : "none" }}>
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px dashed #d9d9d9",
                  borderRadius: 12,
                  minHeight: 240,
                  marginBottom: 8,
                }}>
                {qrTimedOut ? (
                  <Space
                    direction="vertical"
                    align="center"
                    style={{ padding: 16 }}>
                    <CloseCircleFilled
                      style={{ color: "#ff4d4f", fontSize: 48 }}
                    />
                    <Text strong>QR Code Expired</Text>
                    <Text type="secondary" style={{ textAlign: "center" }}>
                      The QR code session has timed out. Please generate a new
                      one.
                    </Text>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={handleRetry}>
                      Retry
                    </Button>
                  </Space>
                ) : qrFailed ? (
                  <Space
                    direction="vertical"
                    align="center"
                    style={{ padding: 16 }}>
                    <CloseCircleFilled
                      style={{ color: "#ff4d4f", fontSize: 48 }}
                    />
                    <Text strong>QR Code Generation Failed</Text>
                    <Text type="secondary" style={{ textAlign: "center" }}>
                      Unable to initialize WhatsApp session. Please contact
                      admin support.
                    </Text>
                  </Space>
                ) : !active ? (
                  <QRCode
                    value={valueQR || "TEST"}
                    size={200}
                    statusRender={customStatusRender}
                    status={loadingQR ? "loading" : "active"}
                  />
                ) : (
                  <QRCode
                    value={"success"}
                    size={200}
                    statusRender={customStatusRender}
                    status={"scanned"}
                  />
                )}
              </div>

              {!active && !qrTimedOut && !qrFailed && (
                <Row justify="center" style={{ marginBottom: 4 }}>
                  <Text type="secondary">
                    QR code refreshes automatically · Expires in{" "}
                    <Text strong>{formatCountdown(remainingSeconds)}</Text>
                  </Text>
                </Row>
              )}

              <Row justify="center" style={{ marginBottom: 4 }}>
                <Text type="secondary">
                  Once scanned, your WhatsApp will be activated and connected to
                  this platform.
                </Text>
              </Row>

              <Form.Item name="clientId" style={{ display: "none" }}>
                <Input />
              </Form.Item>
            </>
          </div>
        </Form>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            {step === 0 ? (
              <Button onClick={onCancel} className="w-100" size="large">
                Cancel
              </Button>
            ) : (
              <Button onClick={handleBack} className="w-100" size="large">
                Back
              </Button>
            )}
          </Col>
          <Col span={12}>
            {" "}
            {step === 0 ? (
              <Button
                size="large"
                type="primary"
                className="w-100"
                disabled={error}
                onClick={handleContinue}>
                Continue
              </Button>
            ) : (
              <Button
                size="large"
                className="w-100"
                type="primary"
                onClick={handleConnect}
                loading={checking}
                disabled={qrFailed || qrTimedOut}>
                I’ve Scanned QR Code
              </Button>
            )}
          </Col>
        </Row>
      </Modal>
      <CustomModal
        type="error"
        title="Activation Failed"
        message="We couldn’t verify your WhatsApp account. Please try scanning the QR code again."
        isVisible={isErrorModalVisible}
        onClose={() => setIsErrorModalVisible(false)}
        successButtonText="Retry"
        onSuccessClick={() => handleConnect()}
        cancelButtonText="Go Back"
        onCancelClick={() => setIsErrorModalVisible(false)}
      />
    </>
  );
};

export default Scan;
